import { useMutation, useQueryClient } from '@tanstack/react-query';

import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';

import { patchWorkspaceSettings } from '../../../api/workspaces/settings/patchWorkspaceSettings';
import { WorkspaceSettings } from '../../../api/workspaces/types';
import { workspaceKeys } from './workspaceQueryKeyFactory';

interface UseUpdateWorkspaceSettingsParams {
  workspaceId: string;
}

export function useUpdateWorkspaceSettings({
  workspaceId,
}: UseUpdateWorkspaceSettingsParams) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patch: Partial<WorkspaceSettings>) =>
      patchWorkspaceSettings({ workspaceId, patch }),
    // The caller (WorkspaceSettings) owns the optimistic cache write — it flips
    // the toggle at click time, *before* the debounced PUT fires, so a snapshot
    // captured here in `onMutate` would already hold the flipped value and
    // rollback would be a no-op. Rollback therefore lives at the call site
    // (`commitPendingUpdate`'s per-call `onError`), which captured the true
    // pre-change snapshot at burst start. Here we only cancel any in-flight
    // refetch so it can't clobber the optimistic value between mutate and settle.
    onMutate: async () => {
      await queryClient.cancelQueries(workspaceKeys.settings(workspaceId));
    },
    onSuccess: () => {
      toast.positive(translate('Successfully saved'));
    },
    onError: () => {
      toast.critical(translate('An error has occurred'));
    },
    onSettled: () => {
      queryClient.invalidateQueries(workspaceKeys.settings(workspaceId));
    },
  });
}
